const ADDITIONAL_DETAILS_MAX = 500

export type PhoneLineType = 'mobile' | 'landline'

export type VoiceAiPhone = {
  countryCode: string
  number: string
}

export type VoiceAiQuestionnaireItem = {
  question: string
  additionalDetails: string | null
}

export type VoiceAiCallPayload = {
  ticket: {
    id: number | null
    title: string
    description: string | null
    metadata: Record<string, unknown>
  }
  contact: {
    firstName: string
    lastName: string
    email: string | null
    phone: VoiceAiPhone
  }
  availability: VoiceAiAvailability
  questionnaire: VoiceAiQuestionnaireItem[]
}

export type VoiceAiAvailability = {
  from: string | null
  until: string | null
}

export const EMPTY_AVAILABILITY: VoiceAiAvailability = { from: null, until: null }

type QuestionTemplate = {
  question: string
  additionalDetails: string | null
  resolveAnswer: (ctx: RowContext) => string
}

type RowContext = {
  contactName: string
  contactNumber: string
  contactEmail: string
  issue: string
  errorMessage: string
  qty: number
  priority: number
  scheduleAt: string
  extracted: Record<string, unknown>
}

function extractedStr(extracted: Record<string, unknown>, key: string): string {
  const v = extracted[key]
  if (v == null) return ''
  return String(v).trim()
}

function resolveDeviceScope(ctx: RowContext): string {
  const fromExtracted = extractedStr(ctx.extracted, 'device_scope')
  if (fromExtracted) return fromExtracted
  if (ctx.qty > 1) return 'Multiple devices'
  if (ctx.qty === 1) return 'One device'
  return ''
}

function resolveTimeSensitive(ctx: RowContext): string {
  const fromExtracted = extractedStr(ctx.extracted, 'time_sensitive')
  if (fromExtracted) return fromExtracted
  return ctx.priority <= 2 ? 'Yes' : 'No'
}

function resolveOnsiteAvailability(ctx: RowContext): string {
  const fromExtracted =
    extractedStr(ctx.extracted, 'onsite_date_text') ||
    extractedStr(ctx.extracted, 'onsite_availability') ||
    extractedStr(ctx.extracted, 'eta_text')
  if (fromExtracted) return fromExtracted
  return ctx.scheduleAt
}

function resolveLocation(ctx: RowContext): string {
  const storeCode = extractedStr(ctx.extracted, 'store_code')
  const location = extractedStr(ctx.extracted, 'location')
  if (storeCode && location) return `${storeCode} — ${location}`
  return storeCode || location
}

const QUESTION_TEMPLATES: QuestionTemplate[] = [
  {
    question: 'What is your first and last name?',
    additionalDetails: 'Confirm spelling matches the ticket contact.',
    resolveAnswer: (ctx) => ctx.contactName,
  },
  {
    question: 'What is your contact number or email?',
    additionalDetails: 'Use the number or email on file if the customer confirms it is correct.',
    resolveAnswer: (ctx) => ctx.contactNumber || ctx.contactEmail,
  },
  {
    question: 'What is your location or address?',
    additionalDetails: 'Include store code, site name, or full address if provided.',
    resolveAnswer: resolveLocation,
  },
  {
    question: 'What exact error message are you seeing?',
    additionalDetails: 'Ask the customer to provide a screenshot or exact wording if possible.',
    resolveAnswer: (ctx) => ctx.errorMessage || ctx.issue,
  },
  {
    question: 'How is this issue affecting your work?',
    additionalDetails: null,
    resolveAnswer: (ctx) => extractedStr(ctx.extracted, 'work_impact'),
  },
  {
    question: 'Is anyone else experiencing the same issue?',
    additionalDetails: null,
    resolveAnswer: (ctx) => extractedStr(ctx.extracted, 'others_affected'),
  },
  {
    question: 'Is this time-sensitive or business-critical?',
    additionalDetails: null,
    resolveAnswer: resolveTimeSensitive,
  },
  {
    question: 'Is the issue on one device or multiple devices?',
    additionalDetails: null,
    resolveAnswer: resolveDeviceScope,
  },
  {
    question: 'Is the issue affecting one user or multiple users?',
    additionalDetails: null,
    resolveAnswer: (ctx) => extractedStr(ctx.extracted, 'user_scope'),
  },
  {
    question: 'Is the issue limited to one application or several applications?',
    additionalDetails: null,
    resolveAnswer: (ctx) => extractedStr(ctx.extracted, 'app_scope'),
  },
  {
    question: 'Is access internal only or external as well?',
    additionalDetails: null,
    resolveAnswer: (ctx) => extractedStr(ctx.extracted, 'access_type'),
  },
  {
    question: 'Is a work permit needed for onsite support?',
    additionalDetails: null,
    resolveAnswer: (ctx) => extractedStr(ctx.extracted, 'work_permit_needed'),
  },
  {
    question: 'How long does it take to process the permit?',
    additionalDetails: null,
    resolveAnswer: (ctx) => extractedStr(ctx.extracted, 'permit_duration'),
  },
  {
    question: 'Is the permit readily available?',
    additionalDetails: null,
    resolveAnswer: (ctx) => extractedStr(ctx.extracted, 'permit_available'),
  },
  {
    question: 'What is your availability for onsite support?',
    additionalDetails: 'Capture date and time window in the customer local timezone if possible.',
    resolveAnswer: resolveOnsiteAvailability,
  },
  {
    question: 'Do you already approve parts replacement?',
    additionalDetails: null,
    resolveAnswer: (ctx) => extractedStr(ctx.extracted, 'parts_approved'),
  },
  {
    question: 'May we close this ticket once the fix is confirmed?',
    additionalDetails: null,
    resolveAnswer: (ctx) => extractedStr(ctx.extracted, 'close_on_fix'),
  },
]

function clampAdditionalDetails(value: string | null): string | null {
  if (value == null || value === '') return null
  const trimmed = value.trim()
  if (!trimmed) return null
  return trimmed.length <= ADDITIONAL_DETAILS_MAX
    ? trimmed
    : trimmed.slice(0, ADDITIONAL_DETAILS_MAX)
}

export function splitContactName(fullName: string): { firstName: string; lastName: string } {
  const parts = fullName.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return { firstName: '', lastName: '' }
  if (parts.length === 1) return { firstName: parts[0], lastName: '' }
  return { firstName: parts[0], lastName: parts.slice(1).join(' ') }
}

/** Infer PH mobile (9xx) vs landline (area code + local) from national digits. */
export function inferPhoneLineType(nationalNumber: string): PhoneLineType {
  const n = nationalNumber.replace(/\D/g, '')
  if (!n) return 'mobile'
  if (/^9\d{9}$/.test(n)) return 'mobile'
  if (n.startsWith('9')) return 'mobile'
  if (/^[2-8]\d{6,9}$/.test(n)) return 'landline'
  return 'mobile'
}

export function parseContactPhone(raw: string): VoiceAiPhone | null {
  const digits = raw.replace(/\D/g, '')
  if (!digits) return null

  let countryCode = '63'
  let number = digits

  if (digits.startsWith('63') && digits.length > 10) {
    number = digits.slice(2)
  } else if (digits.startsWith('0') && digits.length > 6) {
    number = digits.slice(1)
  } else if (digits.length < 7) {
    return null
  }

  const type = inferPhoneLineType(number)
  const minLen = type === 'mobile' ? 10 : 7
  if (number.length < minLen) return null

  return { countryCode, number }
}

export function validatePhoneDigits(type: PhoneLineType, number: string): string | null {
  const n = number.replace(/\D/g, '')
  if (!n) return 'Phone number is required.'
  if (type === 'mobile') {
    if (!/^9\d{9}$/.test(n)) {
      return 'Mobile number must be 10 digits starting with 9 (e.g. 9171234567).'
    }
    return null
  }
  if (n.startsWith('9')) {
    return 'Landline numbers do not start with 9. Choose Mobile if this is a cellphone number.'
  }
  if (n.length < 7 || n.length > 10) {
    return 'Landline must be 7–10 digits including area code (e.g. 28123456 or 321234567).'
  }
  return null
}

export function formatVoiceAiPhone(phone: VoiceAiPhone): string {
  if (!phone.number.trim()) return '–'
  return `+${phone.countryCode} ${phone.number}`
}

function toIsoOrNull(value: unknown): string | null {
  if (value == null || value === '') return null
  const d = new Date(String(value))
  if (Number.isNaN(d.getTime())) return null
  return d.toISOString()
}

/** Convert ISO to `datetime-local` input value (local browser time). */
export function isoToDatetimeLocal(iso: string | null | undefined): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  const h = String(d.getHours()).padStart(2, '0')
  const min = String(d.getMinutes()).padStart(2, '0')
  return `${y}-${m}-${day}T${h}:${min}`
}

/** Parse `datetime-local` as Philippines (+08:00) wall time for the voice AI payload. */
export function datetimeLocalToManilaIso(localValue: string): string | null {
  const trimmed = localValue.trim()
  if (!trimmed) return null
  const [datePart, timePart] = trimmed.split('T')
  if (!datePart || !timePart) return null
  const [y, mo, d] = datePart.split('-').map(Number)
  const [hh, mm] = timePart.split(':').map(Number)
  if ([y, mo, d, hh, mm].some((n) => Number.isNaN(n))) return null
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${y}-${pad(mo)}-${pad(d)}T${pad(hh)}:${pad(mm)}:00+08:00`
}

export function validateAvailabilityRange(
  fromLocal: string,
  untilLocal: string
): string | null {
  const hasFrom = Boolean(fromLocal.trim())
  const hasUntil = Boolean(untilLocal.trim())
  if (!hasFrom && !hasUntil) return null
  if (hasFrom !== hasUntil) {
    return 'Set both availability start and end, or leave both empty.'
  }
  const fromMs = new Date(fromLocal).getTime()
  const untilMs = new Date(untilLocal).getTime()
  if (Number.isNaN(fromMs) || Number.isNaN(untilMs)) return 'Invalid availability date or time.'
  if (untilMs <= fromMs) return 'Availability end must be after the start.'
  return null
}

function buildAvailability(row: Record<string, unknown>): VoiceAiAvailability {
  const fromRaw =
    row.preferred_schedule_at ??
    row.preferred_schedule_from ??
    extractedField(row, 'preferred_schedule_at')
  const untilRaw = row.preferred_schedule_until ?? extractedField(row, 'preferred_schedule_until')

  const fromIso = toIsoOrNull(fromRaw)
  if (!fromIso) return { ...EMPTY_AVAILABILITY }

  const untilIso = toIsoOrNull(untilRaw) ?? addHoursIso(fromIso, 8)
  return {
    from: formatManilaIsoFromDate(new Date(fromIso)),
    until: formatManilaIsoFromDate(new Date(untilIso)),
  }
}

function formatManilaIsoFromDate(date: Date): string {
  const utcMs = date.getTime() + date.getTimezoneOffset() * 60_000
  const manilaMs = utcMs + 8 * 60 * 60_000
  const m = new Date(manilaMs)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${m.getUTCFullYear()}-${pad(m.getUTCMonth() + 1)}-${pad(m.getUTCDate())}T${pad(m.getUTCHours())}:${pad(m.getUTCMinutes())}:${pad(m.getUTCSeconds())}+08:00`
}

function extractedField(row: Record<string, unknown>, key: string): unknown {
  const extracted =
    row.extracted_fields && typeof row.extracted_fields === 'object'
      ? (row.extracted_fields as Record<string, unknown>)
      : {}
  return extracted[key]
}

function addHoursIso(iso: string, hours: number): string {
  const d = new Date(iso)
  d.setHours(d.getHours() + hours)
  return d.toISOString()
}

function buildUnansweredQuestionnaire(ctx: RowContext): VoiceAiQuestionnaireItem[] {
  return QUESTION_TEMPLATES.filter((item) => !String(item.resolveAnswer(ctx) ?? '').trim()).map(
    (item) => ({
      question: item.question,
      additionalDetails: clampAdditionalDetails(item.additionalDetails),
    })
  )
}

export function buildVoiceAiCallPayload(row: Record<string, unknown>): VoiceAiCallPayload {
  const extracted = (
    row.extracted_fields && typeof row.extracted_fields === 'object' ? row.extracted_fields : {}
  ) as Record<string, unknown>

  const contactName = String(row.contact_name ?? extracted.rpa_contact_name ?? '').trim()
  const contactNumber = String(row.contact_number ?? extracted.contact_number ?? '').trim()
  const contactEmail = String(row.contact_email ?? extracted.contact_email ?? '').trim()
  const qty = Number(row.requested_quantity ?? 0)
  const priority = Number(row.priority ?? 3)
  const issue = String(row.description ?? row.email_subject ?? '').trim()
  const errorMessage = extractedStr(extracted, 'error_message')
  const scheduleAt = String(row.preferred_schedule_at ?? extracted.preferred_schedule_at ?? '').trim()
  const title = String(row.email_subject ?? row.request_number ?? 'Support request').trim()
  const serviceType =
    typeof row.service_type === 'string'
      ? row.service_type
      : String((row.service_type as { name?: string } | undefined)?.name ?? '')
  const category = String(extracted.category ?? row.category ?? '').trim()
  const status = row.ticket_status ?? row.status ?? null
  const requestNumber = (row.request_number as string) ?? null
  const clientCode = String(row.client_code ?? '').trim()

  const ctx: RowContext = {
    contactName,
    contactNumber,
    contactEmail,
    issue,
    errorMessage,
    qty,
    priority,
    scheduleAt,
    extracted,
  }

  const { firstName, lastName } = splitContactName(contactName)
  const phone = parseContactPhone(contactNumber) ?? { countryCode: '63', number: '' }

  const metadata: Record<string, unknown> = {
    requestNumber,
    serviceType: serviceType || null,
    category: category || null,
    status,
    clientCode: clientCode || null,
    storeCode: extracted.store_code ?? null,
    priority,
    requestedQuantity: qty > 0 ? qty : null,
    source: 'actionlabs-support-client',
    callMode: row.id != null && String(row.id).trim() !== '' && Number(row.id) !== 0 ? 'followup' : 'new_request',
  }

  return {
    ticket: {
      id: Number(row.id ?? 0) || null,
      title,
      description: issue || null,
      metadata,
    },
    contact: {
      firstName,
      lastName,
      email: contactEmail || null,
      phone,
    },
    availability: buildAvailability(row),
    questionnaire: buildUnansweredQuestionnaire(ctx),
  }
}

export function hasVoiceAiPhone(payload: VoiceAiCallPayload): boolean {
  return Boolean(payload.contact.phone.countryCode.trim() && payload.contact.phone.number.trim())
}

export function applyPhoneToPayload(
  payload: VoiceAiCallPayload,
  countryCode: string,
  number: string,
  type: PhoneLineType
): VoiceAiCallPayload {
  const normalizedCode = countryCode.replace(/\D/g, '').trim() || '63'
  const normalizedNumber = number.replace(/\D/g, '').trim()
  return {
    ...payload,
    contact: {
      ...payload.contact,
      phone: { countryCode: normalizedCode, number: normalizedNumber },
    },
  }
}

export function applyAvailabilityToPayload(
  payload: VoiceAiCallPayload,
  fromLocal: string,
  untilLocal: string
): VoiceAiCallPayload {
  const from = datetimeLocalToManilaIso(fromLocal)
  const until = datetimeLocalToManilaIso(untilLocal)
  return {
    ...payload,
    availability: {
      from,
      until,
    },
  }
}

export function countUnansweredQuestions(row: Record<string, unknown>): number {
  const payload = buildVoiceAiCallPayload(row)
  return payload.questionnaire.length
}

export function formatAvailabilityLabel(availability: VoiceAiAvailability): string {
  if (!availability.from && !availability.until) return '–'
  if (availability.from && availability.until) {
    return `${availability.from} → ${availability.until}`
  }
  return availability.from ?? availability.until ?? '–'
}
